<template>
  <Card cardClasses="border-0 shadow-sm">
    <template #header>
      <div class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0 text-primary">
          <vue-feather type="file-text" size="20" class="me-2"></vue-feather>
          Payroll Details
          <badge class="ms-2 badge-lg" :type="payroll.status === 'draft' ? 'warning' : 'success'">
            <vue-feather :type="payroll.status === 'draft' ? 'edit' : 'check-circle'" size="14" class="me-1"></vue-feather>
            {{ payroll.status }}
          </badge>
          <span class="ms-2 text-muted small">({{payroll.payslips.length}} payslips)</span>
        </h4>
        <div class="d-flex align-items-center">
          <div v-if="profile.role !== 'user'" class="d-flex align-items-center me-3">
            <select data="" class="form-select form-select-sm shadow-sm me-2" v-model="exportType" style="width: auto;">
              <option value="" disabled selected>Select export type</option>
              <optgroup label="PAYROLL">
                <option value="all">ALL</option>
              </optgroup>
              <optgroup label="DEDUCTIONS">
                <option value="nhif">SHIF</option>
                <option value="nssf">NSSF</option>
                <option value="SEC_B">PAYE</option>
              </optgroup>
              <optgroup label="BANKS">
                <option value="co-op-bank">CO-OP BANK</option>
                <option value="equity-bank">EQUITY BANK</option>
                <option value="kcb-bank">KCB BANK</option>
                <option value="ncba-bank">NCBA BANK</option>
                <option value="national-bank">NATIONAL BANK</option>
              </optgroup>
            </select>
            <select data="" class="form-select form-select-sm shadow-sm me-2" v-model="exportFormat" style="width: auto;">
              <option value="" disabled selected>Format</option>
              <option value="xlsx">Excel</option>
              <option value="csv">CSV</option>
            </select>
            <button class="btn btn-sm btn-outline-success" @click="exportPayroll">
              <vue-feather type="download" size="16" class="me-1"></vue-feather>
              Export
            </button>
          </div>
          <button class="btn btn-sm btn-outline-dark me-2" @click="$router.go(-1)">
            <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
            Back
          </button>
          <button class="btn btn-sm btn-outline-primary" @click="printDiv('printable')">
            <vue-feather type="printer" size="16" class="me-1"></vue-feather>
            Print
          </button>
        </div>
      </div>
    </template>
    <div class="row" id="printable">
      <div class="col-md-12">
        <h2 class="text-center mb-4">{{ payroll.code }} - {{ payroll.month }}, {{ payroll.year }}</h2>
      </div>

      <!-- Filter Section -->
      <div class="col-md-12 mb-4 no-print">
        <Card cardClasses="border-0 shadow-sm bg-light">
          <div class="row g-3">
            <div class="col-md-12 mb-2">
              <h5 class="text-primary mb-0">
                <vue-feather type="filter" size="18" class="me-1"></vue-feather>
                Filter Options
              </h5>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label class="form-label fw-medium">Department:</label>
                <select data="" type="text" class="form-select shadow-sm" v-model="filter.department_id" @change="filterPayroll">
                  <option value="" selected>All departments</option>
                  <option v-for="department in departments" :key="department.id" :value="department.id">{{department.name}}</option>
                </select>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label class="form-label fw-medium">Designation:</label>
                <select data="" type="text" class="form-select shadow-sm" v-model="filter.designation_id" @change="filterPayroll">
                  <option value="" selected>All designations</option>
                  <option v-for="designation in designations" :key="designation.id" :value="designation.id">{{designation.name}}</option>
                </select>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label class="form-label fw-medium">Work station:</label>
                <select data="" type="text" class="form-select shadow-sm" v-model="filter.work_station_id" @change="filterPayroll">
                  <option value="" selected>All work stations</option>
                  <option v-for="station in stations" :key="station.id" :value="station.id">{{station.name}}</option>
                </select>
              </div>
            </div>
            <div class="col-md-12 text-end">
              <button class="btn btn-outline-secondary" @click="resetFilter">
                <vue-feather type="refresh-cw" size="16" class="me-1"></vue-feather>
                Reset Filters
              </button>
            </div>
          </div>
        </Card>
      </div>
      <!-- Payroll Data Table -->
      <div class="col-md-12 mb-4">
        <Card cardClasses="border-0 shadow-sm">
          <div class="table-responsive">
            <section slot="pdf-content">
              <div v-if="loading" class="text-center py-5">
                <Spinner spinner-class="mb-1" />
              </div>
              <table v-else class="table table-hover table-striped">
                <thead class="table-light">
                <tr>
                  <th scope="col" class="no-print" width="80"></th>
                  <th scope="col">Employee Code</th>
                  <th scope="col">Employee Name</th>
                  <th scope="col">Salary</th>
                  <th v-for="name in payroll.incomeNames" :key="name" scope="col">
                    {{ name }}
                  </th>
                  <th scope="col" class="table-success">Gross</th>
                  <th scope="col">Taxable</th>
                  <th scope="col">Income Tax</th>
                  <th v-for="name in payroll.deductionNames" :key="name" scope="col">
                    {{ name }}
                  </th>
                  <th scope="col">Tax Relief</th>
                  <th scope="col">SHIF Relief</th>
                  <th scope="col">AHL Relief</th>
                  <th scope="col" class="table-danger">Total Deductions</th>
                  <th scope="col" class="table-primary">Net</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="payslip in payroll.payslips" :key="payslip.id">
                  <td class="no-print">
                    <div class="d-flex">
                      <button class="btn btn-sm btn-outline-info me-1" @click="payslipDetails(payslip.id)" title="View Payslip">
                        <vue-feather type="eye" size="14"></vue-feather>
                      </button>
                      <button
                          v-if="payroll.status === 'draft' && profile.role !== 'user'"
                          class="btn btn-sm btn-outline-danger"
                          @click="confirmDialog('Payslip', () => deletePayslip(payslip.id))"
                          title="Delete Payslip">
                        <vue-feather type="trash-2" size="14"></vue-feather>
                      </button>
                    </div>
                  </td>
                  <td>{{ payslip.employee.employee_no }}</td>
                  <td><a :href="`/employee/${payslip.employee.id}`" class="text-decoration-none">{{ employeeName(payslip.employee) }}</a></td>
                  <td>{{ Ksh.format(payslip.salary) }}</td>
                  <td v-for="income in payslip.sortedIncomes" :key="income.id">
                    {{ Ksh.format(income.amount) }}
                  </td>
                  <td class="table-success fw-medium">{{ Ksh.format(payslip.gross) }}</td>
                  <td>{{ Ksh.format(payslip.taxable) }}</td>
                  <td>{{ Ksh.format(payslip.income_tax) }}</td>
                  <td v-for="deduction in payslip.sortedDeductions" :key="deduction.id">
                    {{ Ksh.format(deduction.amount) }}
                  </td>
                  <td>{{ Ksh.format(payslip.tax_relief) }}</td>
                  <td>{{ Ksh.format(payslip.nhif_relief) }}</td>
                  <td>{{ Ksh.format(payslip.housing_relief) }}</td>
                  <td class="table-danger fw-medium">{{ Ksh.format(employeeAmounts(payslip.deductions)) }}</td>
                  <td class="table-primary fw-medium">{{ Ksh.format(payslip.net) }}</td>
                </tr>
                <tr class="table-secondary">
                  <td class="no-print"></td>
                  <td colspan="2" class="text-uppercase fw-bold">Total:</td>
                  <td class="fw-bold">{{ totalItems(payroll.payslips, 'salary') }}</td>
                  <td v-for="(name, index) in payroll.incomeNames" class="fw-bold">
                    {{ totalEmployeeItems('sortedIncomes', index) }}
                  </td>
                  <td class="table-success fw-bold">{{ totalItems(payroll.payslips, 'gross') }}</td>
                  <td class="fw-bold">{{ totalItems(payroll.payslips, 'taxable') }}</td>
                  <td class="fw-bold">{{ totalItems(payroll.payslips, 'income_tax') }}</td>
                  <td v-for="(name, index) in payroll.deductionNames" class="fw-bold">
                    {{ totalEmployeeItems('sortedDeductions', index) }}
                  </td>
                  <td class="fw-bold">{{ totalItems(payroll.payslips, 'tax_relief') }}</td>
                  <td class="fw-bold">{{ totalItems(payroll.payslips, 'nhif_relief') }}</td>
                  <td class="fw-bold">{{ totalItems(payroll.payslips, 'housing_relief') }}</td>
                  <td class="table-danger fw-bold">{{ calculateTotalDeductions() }}</td>
                  <td class="table-primary fw-bold">{{ totalItems(payroll.payslips, 'net') }}</td>
                </tr>
                </tbody>
              </table>
            </section>
          </div>
        </Card>
      </div>

      <!-- Action Buttons -->
      <div class="col-md-12 mb-4 no-print">
        <Card cardClasses="border-0 shadow-sm">
          <div class="d-flex flex-wrap justify-content-center gap-3">
            <button
                v-if="payroll.status !== 'draft' && profile.role !== 'user' && profile.role !== 'admin' && payroll.payout === null && integrationTypes.includes('bank')"
                class="btn btn-primary"
                @click="confirmDialog('Make Payments', () => createPayouts(), 'Do you want to make payments for this payroll? This will initiate bank payouts.')"
            >
              <vue-feather type="credit-card" size="16" class="me-1"></vue-feather>
              Make Payments
            </button>
            <button
                v-if="payroll.status !== 'draft' && payroll.payout !== null"
                class="btn btn-success"
                @click="$router.push('/payrollPayouts/' + $route.params.id)"
            >
              <vue-feather type="file-text" size="16" class="me-1"></vue-feather>
              Payout Details
            </button>
            <button
                v-if="payroll.status === 'draft' && profile.role !== 'user' && profile.role !== 'admin'"
                class="btn btn-primary"
                @click="confirmDialog('Confirm Payroll', () => confirmPayroll(), 'Do you want to confirm this payroll?')"
            >
              <vue-feather type="check-circle" size="16" class="me-1"></vue-feather>
              Actualize Payroll
            </button>
            <button
                v-if="payroll.status === 'draft' && profile.role !== 'user'"
                class="btn btn-danger"
                @click="confirmDialog('Payroll', () => deletePayroll())"
            >
              <vue-feather type="trash-2" size="16" class="me-1"></vue-feather>
              Delete Payroll
            </button>
          </div>
        </Card>
      </div>
    </div>

  </Card>
</template>

<script>
import payrollService from "@/api/payrollService";
import formatDate from "../../utils/formatDate";
import router from "@/router";
import _ from 'lodash'
import confirmDialog from "@/utils/confirmDialog";
import departmentService from "@/api/departmentService";
import designationService from "@/api/designationService";
import stationService from "@/api/stationService";
import notify from "@/utils/notify";
import {decrypt} from "@/utils/crypto";
import payoutService from "@/api/payoutService";
import loadingButton from "@/utils/loadingButton";
import VueFeather from 'vue-feather';
import Badge from '@/components/badge/Badge.vue';

let Ksh = new Intl.NumberFormat();

export default {
  name: "PayrollDetails",
  components: { VueFeather, Badge },
  data() {
    return {
      profile: {},
      departments: [],
      stations: [],
      designations: [],
      loading: true,
      payroll: {
        incomeNames: [],
        deductionNames: [],
        payslips: [
          {
            employee: {},
            sortedIncomes: [],
            sortedDeductions: [],
            incomes: [],
            deductions: [],
          }
        ],
      },
      filter: {
        department_id: '',
        work_station_id: '',
        designation_id: '',
      },
      exportType: '',
      exportFormat: '',
      Ksh,
      organization: {},
      integrationTypes: [],
    }
  },
  methods: {
    confirmDialog,
    formatDate,
    async getPayroll() {
      this.payroll = await payrollService.getPayroll(this.$route.params.id)
      this.loading = false
    },
    employeeName(employee) {
      return employee.first_name + " " + employee.middle_name + " " + employee.last_name
    },
    payslipDetails(id) {
      router.push('/payslip/' + id)
    },
    totalItems(payslips, item) {
      let total = 0
      for (const payslip of payslips) {
        total += payslip[item]
      }
      return Ksh.format(total)
    },
    employeeAmounts(items) {
      let total = 0
      for (const item of items) {
        total += item.amount
      }
      return total
    },
    totalEmployeeItems(item, key) {
      let allItemsAmounts = []
      for (const payslip of this.payroll.payslips) {
        let allItems = []
        allItems.push(payslip[item][key])
        for (const items of allItems) {
          allItemsAmounts.push(items.amount)
        }
      }
      return Ksh.format(_.sum(allItemsAmounts))
    },
    calculateTotalDeductions() {
      let allItemsAmounts = []
      for (const payslip of this.payroll.payslips) {
        let allItems = []
        allItems.push(payslip.deductions)
        for (const items of allItems) {
          for (const item of items) {
            allItemsAmounts.push(item.amount)
          }
        }
      }
      return Ksh.format(_.sum(allItemsAmounts))
    },
    async confirmPayroll() {
      await payrollService.confirmPayroll(this.$route.params.id)
    },
    async createPayouts() {
      await payoutService.createPayout(this.$route.params.id)
    },
    async deletePayslip(id) {
      await payrollService.deletePayslip(id)
      await this.getPayroll()
    },
    async deletePayroll() {
      await payrollService.deletePayroll(this.$route.params.id)
    },
    async exportPayroll(e) {
      if (this.exportType === '' || this.exportFormat === '') {
        notify.alert.info('Please select export type and format')
      } else {
        loadingButton(e)
        await payrollService.exportPayroll({
              filter: this.filter,
              type: this.exportType,
              extension: this.exportFormat
            },
            this.$route.params.id
        )
        loadingButton(e, false)
      }
    },
    async filterPayroll() {
      this.loading = true
      this.payroll = await payrollService.filterPayroll(this.filter, this.$route.params.id)
      this.loading = false
    },
    async resetFilter() {
      window.location.reload()
    },
    printDiv(divId) {
      let printContents = document.getElementById(divId).innerHTML;
      let originalContents = document.body.innerHTML;
      document.body.innerHTML = printContents;
      window.print();
      document.body.innerHTML = originalContents;
    }
  },
  created: async function () {
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    this.organization.integrations.forEach(integration => {
      this.integrationTypes.push(integration.type)
    })
    await this.getPayroll()
    this.departments = await departmentService.getDepartments()
    this.designations = await designationService.getDesignations()
    this.stations = await stationService.getStations()
    localStorage.setItem('payrollStatus', this.payroll.status)
    document.title = this.payroll.code + '-' + this.payroll.month + '-' + this.payroll.year
  },

}
</script>

<style scoped>
/* Table Styles */
.table-responsive {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

table th {
  font-weight: 600;
  vertical-align: middle;
}

table td {
  white-space: nowrap;
  vertical-align: middle;
}

/* Print Styles */
@media print {
  table th,
  table td {
    white-space: break-spaces !important;
    font-size: 9px !important;
    padding: 1px 1px !important;
    border: 1px solid #dee2e6 !important;
  }

  .table-success {
    background-color: rgba(25, 135, 84, 0.15) !important;
    color: #0f5132 !important;
  }

  .table-primary {
    background-color: rgba(13, 110, 253, 0.15) !important;
    color: #084298 !important;
  }

  .table-danger {
    background-color: rgba(220, 53, 69, 0.15) !important;
    color: #842029 !important;
  }

  .table-secondary {
    background-color: rgba(108, 117, 125, 0.15) !important;
    color: #41464b !important;
  }

  .no-print {
    display: none !important;
  }

  h2 {
    font-size: 16px !important;
    margin-bottom: 10px !important;
  }
}
</style>
